import React, { useEffect, useMemo, useState } from 'react';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertTriangle } from 'lucide-react';
import { getAvailability, AVAILABILITY_LABELS, extractApiError } from '../../lib/subcontractOfferApi';
import { toast } from 'sonner';

const AVAILABILITY_BADGE = {
  libera: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  presente_in_bozza: 'bg-amber-100 text-amber-700 border-amber-300',
  impegnata_da_offerta_attiva: 'bg-rose-100 text-rose-700 border-rose-300',
  assegnata_a_contratto: 'bg-zinc-200 text-zinc-800 border-zinc-400',
  non_ammessa: 'bg-slate-100 text-slate-500 border-slate-300',
};

const BLOCKED = new Set(['impegnata_da_offerta_attiva', 'assegnata_a_contratto', 'non_ammessa']);

/**
 * Selettore delle lavorazioni per il Offerta di Subappalto.
 * Recupera automaticamente le voci dal contratto cliente sorgente del progetto.
 * NON mostra prezzi / totali per riga (regola F.3 §2).
 *
 * Props:
 * - projectId
 * - value: array di customer_contract_item_id selezionati
 * - onChange: (nextArray) => void
 * - showInternalBase: boolean (visibile solo ad admin autorizzati)
 * - excludeOfferId: id dell'offerta corrente in modifica (edit mode) — le voci di questa
 *   offerta NON devono risultare bloccate né mostrate come "presente_in_bozza" verso l'utente
 *   (F.3.1 §4). Il backend rimane comunque autoritativo al salvataggio.
 * - editingOfferItemIds: array di customer_contract_item_id già presenti nella bozza in modifica
 * - testIdPrefix: prefix per data-testid (default 'items-selector')
 */
export const SubcontractOfferItemsSelector = ({
  projectId, value, onChange, showInternalBase = false,
  quantities = {}, onQuantityChange = null,
  excludeOfferId = null, editingOfferItemIds = [], testIdPrefix = 'items-selector',
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAvailability(projectId)
      .then(res => { if (!cancelled) { setData(res); setError(null); } })
      .catch(err => {
        const msg = extractApiError(err, 'Errore caricamento lavorazioni contrattuali');
        if (!cancelled) setError(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  const selected = useMemo(() => new Set(value || []), [value]);
  const editingSet = useMemo(() => new Set(editingOfferItemIds || []), [editingOfferItemIds]);

  /** Ricalcola l'availability effettiva considerando l'esclusione della bozza in modifica. */
  const effectiveAvailability = (it) => {
    if (excludeOfferId && editingSet.has(it.customer_contract_item_id)) {
      // Le voci della bozza in edit vanno considerate "libere" nell'UI:
      // - se il backend le mostra impegnata_da_offerta_attiva è per l'auto-impegno della stessa bozza
      // - se sono presente_in_bozza è la stessa bozza corrente
      // Il salvataggio è comunque autoritativo lato backend.
      return 'libera';
    }
    return it.availability;
  };

  const toggle = (item) => {
    const av = effectiveAvailability(item);
    if (BLOCKED.has(av)) return;
    const cid = item.customer_contract_item_id;
    const next = new Set(selected);
    if (next.has(cid)) next.delete(cid);
    else {
      next.add(cid);
      // Residuo: default della quantità offerta = residuo riaffidabile (cap).
      if (item.is_residual && onQuantityChange && quantities[cid] == null) {
        onQuantityChange(cid, item.available_quantity);
      }
    }
    onChange(Array.from(next));
  };

  const selectedCount = value?.length || 0;

  const baseInternalPreview = useMemo(() => {
    if (!data) return null;
    let total = 0;
    for (const it of data.items || []) {
      if (selected.has(it.customer_contract_item_id)) {
        if (it.is_residual) {
          const q = quantities[it.customer_contract_item_id];
          const qty = (q === '' || q == null) ? Number(it.available_quantity) || 0 : Number(q) || 0;
          total += qty * (Number(it.internal_unit_price) || 0);
        } else {
          total += Number(it.internal_line_total) || 0;
        }
      }
    }
    return total;
  }, [data, selected, quantities]);

  if (loading) {
    return <div className="p-6 text-sm text-slate-500" data-testid="items-selector-loading">Caricamento lavorazioni contrattuali...</div>;
  }
  if (error) {
    return (
      <Alert variant="destructive" data-testid="items-selector-error">
        <AlertTriangle className="w-4 h-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }
  if (!data || (data.items || []).length === 0) {
    return <div className="p-6 text-sm text-slate-500">Nessuna lavorazione contrattuale disponibile.</div>;
  }

  return (
    <div className="space-y-3" data-testid={testIdPrefix}>
      <div className="text-sm text-slate-600">
        Fonte lavorazioni: contratto cliente
        {data.customer_contract_id && <span className="ml-1 font-mono text-xs text-slate-500">({data.customer_contract_id.slice(0, 8)}…)</span>}
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid={`${testIdPrefix}-table`}>
          <thead className="bg-slate-50 text-slate-700">
            <tr>
              <th className="p-2 text-left w-10"></th>
              <th className="p-2 text-left">Categoria</th>
              <th className="p-2 text-left">Codice</th>
              <th className="p-2 text-left">Descrizione</th>
              <th className="p-2 text-left">U.M.</th>
              <th className="p-2 text-right">Quantità</th>
              <th className="p-2 text-left">Stato</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((it) => {
              const effAvail = effectiveAvailability(it);
              const isBlocked = BLOCKED.has(effAvail);
              const isSelected = selected.has(it.customer_contract_item_id);
              const isDraftWarn = effAvail === 'presente_in_bozza';
              const isEditingItem = editingSet.has(it.customer_contract_item_id);
              return (
                <tr
                  key={it.customer_contract_item_id}
                  className={`border-t border-slate-200 ${isBlocked ? 'bg-slate-50 opacity-70' : 'hover:bg-slate-50'} ${isSelected ? 'bg-orange-50' : ''}`}
                  data-testid={`${testIdPrefix}-row-${it.customer_contract_item_id}`}
                >
                  <td className="p-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isBlocked}
                      onChange={() => toggle(it)}
                      data-testid={`${testIdPrefix}-checkbox-${it.customer_contract_item_id}`}
                      className="w-4 h-4"
                    />
                  </td>
                  <td className="p-2">{it.category || '—'}</td>
                  <td className="p-2 font-mono text-xs">{it.code || '—'}</td>
                  <td className="p-2">
                    <div>{it.description}</div>
                    {isEditingItem && (
                      <div className="text-xs text-orange-700 mt-1" data-testid={`${testIdPrefix}-in-current-draft-${it.customer_contract_item_id}`}>
                        Voce già presente in questa bozza
                      </div>
                    )}
                    {isDraftWarn && isSelected && !isEditingItem && (
                      <div className="text-xs text-amber-700 mt-1 flex items-center gap-1" data-testid={`${testIdPrefix}-draft-warning`}>
                        <AlertTriangle className="w-3 h-3" />
                        Voce già presente in un&apos;altra bozza.
                      </div>
                    )}
                  </td>
                  <td className="p-2">{it.unit || '—'}</td>
                  <td className="p-2 text-right align-top">
                    <div className="flex flex-col items-end gap-1">
                      <span className={it.is_residual ? 'text-slate-400 line-through' : ''}>{it.quantity}</span>
                      {it.is_residual && (
                        <span
                          className="text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 whitespace-nowrap"
                          data-testid={`${testIdPrefix}-residual-${it.customer_contract_item_id}`}
                        >
                          Residuo: {it.available_quantity}
                        </span>
                      )}
                      {it.is_residual && isSelected && !isBlocked && (
                        <input
                          type="number"
                          step="any"
                          min="0"
                          max={it.available_quantity}
                          value={quantities[it.customer_contract_item_id] ?? it.available_quantity}
                          onChange={(e) => {
                            if (!onQuantityChange) return;
                            const raw = e.target.value;
                            let v = raw === '' ? '' : Number(raw);
                            if (typeof v === 'number' && !Number.isNaN(v)) {
                              if (v < 0) v = 0;
                              if (v > it.available_quantity) v = it.available_quantity;
                            }
                            onQuantityChange(it.customer_contract_item_id, v);
                          }}
                          className="w-24 border border-slate-300 rounded px-2 py-1 text-sm text-right"
                          data-testid={`${testIdPrefix}-qty-${it.customer_contract_item_id}`}
                        />
                      )}
                    </div>
                  </td>
                  <td className="p-2">
                    <div className="flex flex-col gap-1">
                      <Badge className={AVAILABILITY_BADGE[effAvail] || ''} data-testid={`${testIdPrefix}-status-${it.customer_contract_item_id}`}>
                        {AVAILABILITY_LABELS[effAvail] || effAvail}
                      </Badge>
                      {it.detail?.offer_number && !isEditingItem && (
                        <span className="text-xs text-slate-500" data-testid={`${testIdPrefix}-ref-${it.customer_contract_item_id}`}>
                          Offerta: {it.detail.offer_number}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm">
        <span data-testid={`${testIdPrefix}-count`}>
          Voci selezionate: <b>{selectedCount}</b>
        </span>
        {showInternalBase && baseInternalPreview !== null && (
          <span className="text-slate-600" data-testid={`${testIdPrefix}-base-internal`}>
            Valore base interno stimato: € {baseInternalPreview.toFixed(2)}
          </span>
        )}
      </div>
      {/* Marker per l'elenco delle voci selezionate — usato dai test edit */}
      <div style={{ display: 'none' }} data-testid="subcontract-offer-edit-selected-items">
        {(value || []).join(',')}
      </div>
    </div>
  );
};

export default SubcontractOfferItemsSelector;
